import { useContext, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import ShellAuthContext from './AuthContext.js';
import { tokenService } from './tokenService.js';
import { decodeJwtPayload } from './jwtUtils.js';
import { isSessionStaleAfterGlobalLogout } from './globalLogout.js';
import { getCachedProfile } from './profileCache.js';

/**
 * Unified auth hook that works in both standalone and shell-embedded contexts.
 *
 * - When embedded in the Shell: reads token/user from ShellAuthContext.
 * - When running standalone: OUR OWN backend JWT is the real authentication
 *   gate (see hasValidBackendSession below) — not Auth0 SDK's own
 *   `isAuthenticated`. Auth0 is only ever consulted once, for identity;
 *   after that, moving between {slug}.{rootDomain} workspace subdomains
 *   hands our JWT directly to the new origin (see tokenHandoff.js), and
 *   Auth0 SDK's per-origin session simply has nothing to say about whether
 *   that origin is authenticated.
 *
 * @returns {{ isAuthenticated: boolean, user: object|null, token: string|null, isLoading: boolean, loginWithRedirect: Function, logout: Function, getAccessTokenSilently: Function, error: Error|null }}
 */

/**
 * True when this ORIGIN's own cached backend JWT was issued before the last
 * "logout everywhere" marker (see globalLogout.js) — i.e. this origin's
 * locally cached session is stale relative to a logout that happened on a
 * different {slug}.{rootDomain} subdomain, even though neither Auth0's own
 * SDK cache nor our JWT has actually expired yet on this origin.
 */
function isLocallySessionStale() {
  const iat = decodeJwtPayload(tokenService.getToken())?.iat;
  return isSessionStaleAfterGlobalLogout(iat);
}

/**
 * True when `token` is a currently valid (unexpired, not globally logged
 * out) backend JWT. This is the real authentication gate in standalone
 * mode — see the hook's own doc comment above for why Auth0 SDK's
 * `isAuthenticated` can't fill that role by itself anymore.
 */
function isValidBackendSession(token) {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return false;
  if (payload.exp * 1000 <= Date.now()) return false;
  return !isLocallySessionStale();
}

const useAuth = () => {
  const shellAuth = useContext(ShellAuthContext);

  // Embedded mode — shell has already authenticated
  if (shellAuth !== null) {
    return {
      isAuthenticated: shellAuth.isAuthenticated && !isLocallySessionStale(),
      user: shellAuth.user,
      token: shellAuth.token,
      isLoading: false,
      loginWithRedirect: () => {},
      logout: () => {},
      getAccessTokenSilently: shellAuth.getAccessTokenSilently || (async () => shellAuth.token),
      error: null,
    };
  }

  // Standalone mode
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const auth0 = useAuth0();

  // Reactive mirror of tokenService's current token. Reading
  // tokenService.getToken() directly (a plain localStorage read) is NOT
  // enough on its own: nothing re-renders consumers of useAuth() when
  // tokenService.setToken() fires elsewhere (Auth0AuthBridge's exchange, a
  // workspace switch, a cross-subdomain handoff) — so a component that
  // read the token on one render would keep seeing that same stale value
  // forever, until something UNRELATED happened to force a re-render. That
  // gap is exactly what made WorkspaceGate decide "nowhere to go" right
  // after login (token not written yet) and never reconsider once the real
  // token actually landed a moment later — only a manual refresh picked it
  // up. Subscribing here makes every setToken() call propagate immediately.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [token, setTokenState] = useState(() => tokenService.getToken());
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => tokenService.onTokenChange(setTokenState), []);

  const validBackendSession = isValidBackendSession(token);

  return {
    // Our own JWT is the real gate — see isValidBackendSession above.
    // Falls back to Auth0's own isAuthenticated only for the brief window
    // before the very first token exchange completes (no backend JWT yet
    // at all), so loading/redirect states still behave sanely during that
    // one-time identity login.
    isAuthenticated: validBackendSession || (auth0.isAuthenticated && !isLocallySessionStale()),
    // Prefer Auth0's own `user` (richer, present on the origin where the
    // identity login actually happened); fall back to the cached profile
    // captured during a cross-subdomain handoff (see tokenHandoff.js) when
    // Auth0 SDK has no session on this particular origin.
    user: validBackendSession ? (auth0.user || getCachedProfile()) : auth0.user,
    token,
    // Genuinely still resolving auth when there's no valid backend session
    // yet AND either Auth0 itself hasn't finished its own check, OR Auth0
    // says we're identified but our own backend token exchange (the thing
    // that actually produces a usable session) hasn't landed yet. Without
    // that second condition, ProtectedRoute/WorkspaceGate would let the
    // user through the instant Auth0 itself settles — even though our own
    // JWT (and its workspace_slug claim) doesn't exist yet.
    isLoading: !validBackendSession && (auth0.isLoading || auth0.isAuthenticated),
    loginWithRedirect: auth0.loginWithRedirect,
    logout: auth0.logout,
    getAccessTokenSilently: auth0.getAccessTokenSilently,
    error: auth0.error || null,
  };
};

export default useAuth;
